// =============================================================================
// BillingAlertBanner — global wholesale payment warning (Portal)
// =============================================================================
// Live-reads the org's wholesale payment readiness snapshot and renders the
// most severe applicable warning above the page content on EVERY page. The
// banner derives from live state, so it disappears automatically once the
// condition resolves (card added, wallet funded, held orders retried) — no
// dismiss state on purpose: these are action-required warnings.
//
// Alert precedence (one banner at a time):
//   1. walletFrozen        → danger  (charges blocked entirely)
//   2. onHoldOrderCount    → danger  (orders already held on payment)
//   3. paymentSetupMissing → warning (charges pending, no way to pay — only
//      raised while at least one order awaits collection, never a standing
//      "no card on file" nag)
//
// The endpoint requires `wallet:read`; the query is skipped entirely for
// users without it so we never surface 403 noise.
// =============================================================================

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { hasAnyPermission, type BillingAlertsDto } from '@tkpod/shared';

import { api } from '@/lib/api';
import { useAuth } from '@/hooks/use-auth';

const POLL_INTERVAL_MS = 60_000;

interface AlertContent {
  tone: 'warning' | 'danger';
  title: string;
  body: string;
  ctaLabel: string;
  ctaTo: string;
}

function resolveAlert(alerts: BillingAlertsDto): AlertContent | null {
  if (alerts.walletFrozen) {
    return {
      tone: 'danger',
      title: 'Wallet frozen',
      body:
        'Your wallet is frozen and order charges cannot be processed. ' +
        'Review your wallet for details, or contact support if you believe this is an error.',
      ctaLabel: 'Open wallet',
      ctaTo: '/billing?tab=wallet',
    };
  }
  if (alerts.onHoldOrderCount > 0) {
    const n = alerts.onHoldOrderCount;
    return {
      tone: 'danger',
      title: `${n} order${n === 1 ? '' : 's'} on hold — payment required`,
      body:
        'These orders will not enter production until their wholesale charge is settled. ' +
        'Add or update a card, or top up your wallet — held orders retry automatically.',
      ctaLabel: 'Set up payment',
      ctaTo: '/billing?tab=wallet',
    };
  }
  if (alerts.paymentSetupMissing) {
    const n = alerts.pendingChargeOrderCount;
    return {
      tone: 'warning',
      title: 'Payment setup required',
      body:
        `${n} order${n === 1 ? ' is' : 's are'} awaiting payment, but there is ` +
        'no payment method on file and your wallet balance is empty. ' +
        'Add a card or top up your wallet so these orders can be charged and enter production.',
      ctaLabel: 'Set up payment',
      ctaTo: '/billing?tab=wallet',
    };
  }
  return null;
}

const TONE_STYLES: Record<AlertContent['tone'], string> = {
  warning: 'border-yellow-200 bg-yellow-50 text-yellow-800',
  danger: 'border-danger-200 bg-danger-50 text-danger-800',
};

export function BillingAlertBanner() {
  const { user } = useAuth();
  const permissions = user?.currentOrganization?.permissions ?? [];
  const canRead = hasAnyPermission(permissions, ['wallet:read', 'wallet:manage']);

  const alertsQuery = useQuery<BillingAlertsDto, Error>({
    queryKey: ['billing', 'alerts'],
    queryFn: () => api.get<BillingAlertsDto>('/api/v1/billing/alerts'),
    enabled: canRead,
    staleTime: 30_000,
    refetchInterval: POLL_INTERVAL_MS,
  });

  if (!canRead || !alertsQuery.data) return null;
  const alert = resolveAlert(alertsQuery.data);
  if (!alert) return null;

  return (
    <div
      role="alert"
      className={`flex flex-wrap items-start justify-between gap-3 rounded-lg border px-4 py-3 ${TONE_STYLES[alert.tone]}`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{alert.title}</p>
        <p className="mt-0.5 text-sm">{alert.body}</p>
      </div>
      <Link
        to={alert.ctaTo}
        className="shrink-0 rounded-md border border-current px-3 py-1.5 text-xs font-semibold transition-opacity hover:opacity-80"
      >
        {alert.ctaLabel}
      </Link>
    </div>
  );
}
